import WebLayout from "../layout/WebLayout";
import { FaRupeeSign, FaCheckCircle, FaUpload } from "react-icons/fa";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  defaultProductImage,
  MSME_USER_ADDRESS,
  MSME_USER_ORDER_DETAILS,
  PAYMENT_DETAILS,
} from "../utils/Constants";
import { Link, useNavigate } from "react-router-dom";
import toast from "react-hot-toast";
import { useDispatch, useSelector } from "react-redux";
import * as authActions from "../store/action/auth";
import { useCartFetch } from "../hooks/useCartFetch";
import * as checkoutActions from "../store/action/checkout";
import BtnLoader from "../components/BtnLoader";
import { useStates } from "../hooks/useStates";
import { useCities } from "../hooks/useCities";
import { useBankDetailsFetch } from "../hooks/useBankDetailsFetch";
import { getCookie, removeCookie } from "../utils/CookieAction";
import { useCodeFetch } from "../hooks/useCodeFetch";
import { use } from "react";

// Reusable Input Component
const InputField = ({
  name,
  label,
  type,
  value,
  onChange,
  required,
  placeholder,
  maxLength,
  readOnly = false,
}) => (
  <div className="form-group mb-5 mt-5">
    <label className="mb-5">
      {label}
      {required && <span className="text-danger">*</span>}
    </label>
    <input
      name={name}
      type={type}
      className="common-input border-gray-100"
      placeholder={placeholder}
      value={value}
      onChange={onChange}
      required={required}
      maxLength={maxLength}
      readOnly={readOnly}
    />
  </div>
);

// Reusable Select Component
const SelectField = ({ name, label, value, onChange, options, required }) => (
  <div className="form-group mb-5 mt-5">
    <label className="mb-5">
      {label}
      {required && <span className="text-danger">*</span>}
    </label>
    <select
      name={name}
      className="common-input px-16"
      value={value}
      onChange={onChange}
      required={required}>
      <option value="" disabled selected>
        Select {label}
      </option>
      {options.map((item) => (
        <option key={item.id} value={item.id}>
          {item.name}
        </option>
      ))}
    </select>
  </div>
);

function Checkout() {
  const { accessToken, storeCity, accountExistAnce, code } = useSelector(
    (state) => state.auth
  );
  const { cartList, minimumOrderAmount } = useSelector((state) => state.cart);
  const { stateList, city } = useSelector((state) => state.menu);
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const [isLoader, setIsLoader] = useState(false);
  const [loginToggle, setLoginToggle] = useState(true);
  const [registrationToggle, setRegistrationToggle] = useState(false);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [paymentMethod, setPaymentMethod] = useState("");
  const [orderSuccess, setOrderSuccess] = useState(false);
  const [selectedBank, setSelectedBank] = useState("");
  const [screenshotFile, setScreenshotFile] = useState(null);
  const [screenshotPreview, setScreenshotPreview] = useState(null);
  const [paymentId, setPaymentId] = useState();

  const [formState, setFormState] = useState({
    qty: 0,
    price: 0,
    tax: 0,
    pv: 0,
    name: "",
    businessName: "",
    gstNo: "",
    referBy: "",
    email: "",
    state: "",
    district: "",
    pincode: "",
    address: "",
    mobile: "",
    password: "",
    transactionId: "",
    canteen_type: "",
    canteen_code: "",
  });

  useBankDetailsFetch(storeCity);
  useCartFetch(storeCity);
  useStates();
  useCities(formState.state);
  useCodeFetch(formState.canteen_type);

  const { bankDetails } = useSelector((state) => state.checkout);

  useEffect(() => {
    const data = getCookie(MSME_USER_ORDER_DETAILS);
    if (data) {
      const parsedData = JSON.parse(data);

      // Check if order total is less than minimum order amount
      if (parsedData.cartTotalPrice < minimumOrderAmount) {
        toast.error(`Minimum order amount is ₹${minimumOrderAmount}`);
        navigate("/");
        return;
      }

      setFormState((prev) => ({
        ...prev,
        qty: parsedData.cartTotalQty,
        price: parsedData.cartTotalPrice,
        tax: parsedData.cartTax,
        pv: parsedData.pv,
      }));
    } else {
      navigate("/");
    }

    const savedPayment = getCookie(PAYMENT_DETAILS);
    if (savedPayment) {
      const paymentData = JSON.parse(savedPayment);
      setPaymentMethod(paymentData.method);
      setFormState((prev) => ({
        ...prev,
        transactionId: paymentData.transactionId,
      }));
      if (paymentData.bankAccount) {
        setSelectedBank(paymentData.bankAccount);
      }
      if (paymentData.screenshot) {
        setScreenshotPreview(paymentData.screenshot);
      }
    }
  }, [minimumOrderAmount, navigate]);

  const handleInputChange = useCallback((e) => {
    const { name, value } = e.target;
    setFormState((prev) => ({ ...prev, [name]: value }));
  }, []);

  const handleScreenshotUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 2 * 1024 * 1024) {
        toast.error("File size should be less than 2MB");
        return;
      }
      setScreenshotFile(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setScreenshotPreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const verifyAndPlaceOrder = async () => {
    setIsLoader(true);
    try {
      const response = await dispatch(
        checkoutActions.createOrder(
          storeCity,
          paymentMethod,
          formState.transactionId,
          screenshotFile
        )
      );
      if (response && response.paymentId) {
        setPaymentId(response.paymentId);
        return response.paymentId;
      }
    } catch (error) {
      toast.error(error.message);
    } finally {
      setIsLoader(false);
    }
  };

  const handlePayNow = async () => {
    setIsLoader(true);
    try {
      await dispatch(
        checkoutActions.payNow(
          paymentId,
          paymentMethod,
          formState.transactionId,
          screenshotFile
        )
      );

      removeCookie(MSME_USER_ORDER_DETAILS);
      removeCookie(MSME_USER_ADDRESS);
      removeCookie(PAYMENT_DETAILS);
      setOrderSuccess(true);
    } catch (error) {
      toast.error(error.message);
    } finally {
      setIsLoader(false);
      setShowPaymentModal(false);
    }
  };

  const closePaymentModal = () => {
    setShowPaymentModal(false);
  };

  const handleSubmit = useCallback(
    async (e, action, successMessage) => {
      e.preventDefault();
      setIsLoader(true);
      try {
        await dispatch(action);
        toast.success(successMessage);
        await dispatch(checkoutActions.getBankDetails(storeCity));
        setIsLoader(false);
      } catch (error) {
        toast.error(error.message);
        setIsLoader(false);
      }
    },
    [dispatch, storeCity]
  );

  const totalPrice = useMemo(
    () => formState.price?.toFixed(2),
    [formState.price]
  );
  const subtotal = useMemo(
    () => (formState.price - formState.tax)?.toFixed(2),
    [formState.price, formState.tax]
  );
  const taxAmount = useMemo(() => formState.tax?.toFixed(2), [formState.tax]);

  useEffect(() => {
    if (formState.mobile.length > 9) {
      dispatch(authActions.accountExistAnce(formState.mobile));
    }
  }, [dispatch, formState.mobile]);

  const canteenType = [
    {
      value: "ASC",
      name: "ASC",
    },
    {
      value: "ACS",
      name: "ACS",
    },
    {
      value: "APC",
      name: "APC",
    },
    {
      value: "Other",
      name: "Other",
    },
  ];

  const handleProceedToPay = () => {
    verifyAndPlaceOrder();
    setShowPaymentModal(true);
  };

  return (
    <WebLayout pageName="Checkout">
      <section className="checkout py-20">
        <div className="container container-lg">
          <div className="row">
            <div className="col-xl-8 col-lg-8">
              <div className="card mb-10">
                <div className="card-header p-14 d-flex justify-content-between">
                  <h4 className="text-xl m-0">Login and Registration</h4>
                  <Link
                    to="#"
                    className="btn btn-success py-6"
                    onClick={() => setLoginToggle(!loginToggle || accessToken)}>
                    <i
                      className={`fa ${
                        loginToggle && !accessToken
                          ? "fa-angle-down"
                          : "fa-angle-right"
                      }`}></i>
                  </Link>
                </div>
                <div className="card-body p-0">
                  {loginToggle && !accessToken && (
                    <div className="card rounded-0">
                      <div className="card-body">
                        {accountExistAnce === "Registered" ||
                        accountExistAnce === "" ? (
                          <form
                            onSubmit={(e) =>
                              handleSubmit(
                                e,
                                authActions.login(
                                  formState.mobile,
                                  formState.password
                                ),
                                "Login Successfully"
                              )
                            }>
                            <div className="row gy-3">
                              <InputField
                                name={"mobile"}
                                label="Mobile Number"
                                type="text"
                                value={formState.mobile}
                                onChange={handleInputChange}
                                required
                                placeholder="Mobile Number"
                                maxLength={10}
                              />
                              {accountExistAnce === "Registered" ? (
                                <>
                                  <InputField
                                    name={"password"}
                                    label="Password"
                                    type="password"
                                    value={formState.password}
                                    onChange={handleInputChange}
                                    required
                                    placeholder="Password"
                                  />
                                  <button
                                    disabled={isLoader}
                                    className="btn btn-main mt-20 py-18 w-100 rounded-8">
                                    Login
                                  </button>{" "}
                                </>
                              ) : null}
                            </div>
                          </form>
                        ) : null}
                        {accountExistAnce === "Not Registered" ? (
                          <form
                            onSubmit={(e) =>
                              handleSubmit(
                                e,
                                authActions.register({ ...formState }),
                                "Register Successfully"
                              )
                            }>
                            <div className="row gy-3">
                              <SelectField
                                name="canteen_type"
                                label="Buyer Type"
                                value={formState.canteen_type}
                                onChange={handleInputChange}
                                options={canteenType}
                                required
                              />
                              {formState.canteen_type != "Other" ? (
                                <SelectField
                                  name="canteen_code"
                                  label={`${formState.canteen_type} Code`}
                                  value={formState.canteen_code}
                                  onChange={handleInputChange}
                                  options={code}
                                  required={formState.canteen_type != "Other"}
                                />
                              ) : (
                                ""
                              )}
                              <InputField
                                name={"name"}
                                label="Name"
                                type="text"
                                value={formState.name}
                                onChange={handleInputChange}
                                required
                                placeholder="Name"
                              />
                              <InputField
                                name={"businessName"}
                                label="Business Name"
                                type="text"
                                value={formState.businessName}
                                onChange={handleInputChange}
                                required
                                placeholder="Business Name"
                              />
                              <InputField
                                name={"gstNo"}
                                label="GST No"
                                type="text"
                                value={formState.gstNo}
                                onChange={handleInputChange}
                                required
                                placeholder="GST No"
                              />
                              <InputField
                                name={"email"}
                                label="Email Id"
                                type="email"
                                value={formState.email}
                                onChange={handleInputChange}
                                required
                                placeholder="Email Id"
                              />
                              <InputField
                                name={"mobile"}
                                label="Mobile No."
                                type="text"
                                value={formState.mobile}
                                onChange={handleInputChange}
                                required
                                placeholder="Mobile No."
                                maxLength={10}
                              />
                              <InputField
                                name={"password"}
                                label="Password"
                                type="password"
                                value={formState.password}
                                onChange={handleInputChange}
                                required
                                placeholder="Password"
                              />
                              <SelectField
                                name={"state"}
                                label="Select State"
                                value={formState.state}
                                onChange={handleInputChange}
                                options={stateList}
                                required
                              />
                              <SelectField
                                name={"district"}
                                label="Select District"
                                value={formState.district}
                                onChange={handleInputChange}
                                options={city}
                                required
                              />
                              <InputField
                                name={"pincode"}
                                label="Pincode"
                                type="text"
                                value={formState.pincode}
                                onChange={handleInputChange}
                                required
                                placeholder="Pincode"
                              />
                              <div className="col-sm-12 col-xs-12">
                                <label className="mb-5">
                                  Address
                                  <span className="text-danger">*</span>
                                </label>
                                <textarea
                                  name={"address"}
                                  className="common-input border-gray-100"
                                  placeholder="Address"
                                  value={formState.address}
                                  onChange={handleInputChange}></textarea>
                              </div>
                              <div className="col-md-12">
                                <button
                                  type="submit"
                                  className="btn btn-main mt-10 py-18 w-100 rounded-8">
                                  Register
                                </button>
                              </div>
                            </div>
                          </form>
                        ) : null}
                      </div>
                    </div>
                  )}
                </div>
              </div>
              <div className="card">
                <div className="card-header p-14 d-flex justify-content-between">
                  <h4 className="text-xl m-0">Order Items</h4>
                  <Link
                    to="#"
                    className="btn btn-success py-6"
                    onClick={() =>
                      setRegistrationToggle(!registrationToggle || !accessToken)
                    }>
                    <i
                      className={`fa ${
                        !registrationToggle && accessToken
                          ? "fa-angle-down"
                          : "fa-angle-right"
                      }`}></i>
                  </Link>
                </div>
                <div className="card-body">
                  {(registrationToggle || accessToken) && (
                    <div className="cart-table border border-gray-100 rounded-8 px-20 py-20">
                      <div className="overflow-x-auto scroll-sm scroll-sm-horizontal">
                        {Array.isArray(cartList) &&
                          cartList.map((item) => (
                            <div
                              key={item.product_id}
                              className="table-product d-flex gap-24 border-bottom py-10">
                              <Link
                                to={`/product-details/${item.product_slug}?product_id=${item.product_id}`}
                                className="table-product__thumb border border-gray-100 rounded-8 flex-center">
                                <img
                                  src={item.product_image}
                                  className="h-50 w-50"
                                  alt="img"
                                  onError={(e) =>
                                    (e.target.src = defaultProductImage)
                                  }
                                />
                              </Link>
                              <div className="table-product__content text-start">
                                <h6 className="title text-lg fw-semibold mb-8">
                                  <Link
                                    to={`/product-details/${item.product_slug}?product_id=${item.product_id}`}
                                    className="link text-line-2"
                                    tabIndex="0">
                                    {item.product_name}
                                  </Link>
                                </h6>

                                <div className="flex-align gap-16">
                                  <input
                                    className="form-control h-40 w-40"
                                    type="hidden"
                                    value={item.qty}
                                    readOnly
                                  />
                                  <div
                                    style={{
                                      fontSize: 16,
                                      fontWeight: "bold",
                                    }}>
                                    Qty : {item.qty}
                                  </div>
                                  X
                                  <div
                                    style={{
                                      fontSize: 16,
                                      fontWeight: "bold",
                                    }}>
                                    <FaRupeeSign />
                                    {Number(item.sell_price)?.toFixed(2)}
                                  </div>
                                  =
                                  <div
                                    style={{
                                      fontSize: 16,
                                      fontWeight: "bold",
                                    }}>
                                    <FaRupeeSign />
                                    {item.totalPrice?.toFixed(2)}
                                  </div>
                                </div>
                              </div>
                            </div>
                          ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>

            <div className="col-xl-4 col-lg-4 mt-0">
              <div className="cart-sidebar border border-gray-100 rounded-8 px-24 py-24">
                <h6 className="text-xl">PRICE DETAILS</h6>
                <div className="bg-color-three rounded-8 p-20">
                  <div className="flex-between align-items-center">
                    <h6 style={{ fontSize: 16 }}>Total Items</h6>
                    <span className="text-gray-900 fw-semibold">
                      <span>{formState.qty}</span>
                    </span>
                  </div>

                  <div className="flex-between align-items-center">
                    <h6 style={{ fontSize: 16 }}>Subtotal</h6>
                    <span className="text-gray-900 fw-semibold">
                      <FaRupeeSign />
                      <span>{Number(subtotal)?.toFixed(2)}</span>
                    </span>
                  </div>

                  <div className="flex-between align-items-center">
                    <h6 style={{ fontSize: 16 }}>Taxes</h6>
                    <span className="text-gray-900 fw-semibold">
                      <FaRupeeSign />
                      <span>{Number(taxAmount)?.toFixed(2)}</span>
                    </span>
                  </div>
                </div>
                <div className="bg-color-three rounded-8 p-20 mt-20">
                  <div className="flex-between gap-8">
                    <span className="text-gray-900 text-xl fw-semibold">
                      <strong>Total</strong>
                    </span>
                    <span className="text-gray-900 text-xl fw-semibold">
                      <FaRupeeSign />
                      {Number(totalPrice)?.toFixed(2)}
                    </span>
                  </div>
                </div>
                {(registrationToggle || accessToken) && (
                  <div className="mt-20">
                    <h6 className="text-xl">SELECT PAYMENT METHOD</h6>
                    <div className="row">
                      <div className="col-md-6 mb-10">
                        <div
                          className={`payment-option-card rounded-8 border ${
                            paymentMethod === "qr"
                              ? "border-primary"
                              : "border-gray-100"
                          }`}
                          onClick={() => setPaymentMethod("qr")}
                          style={{ padding: "5px" }}>
                          <div className="form-check">
                            <input
                              className="form-check-input"
                              type="radio"
                              name="paymentMethod"
                              id="qr"
                              checked={paymentMethod === "qr"}
                              readOnly
                            />
                            <label
                              className="form-check-label fw-bold"
                              htmlFor="qr">
                              QR Code Payment
                            </label>
                          </div>
                          <p className="text-muted small mt-5 mb-0">
                            Scan and pay via UPI
                          </p>
                        </div>
                      </div>

                      <div className="col-md-6 mb-10">
                        <div
                          className={`payment-option-card p-15 rounded-8 border ${
                            paymentMethod === "bank"
                              ? "border-primary"
                              : "border-gray-100"
                          }`}
                          onClick={() => setPaymentMethod("bank")}
                          style={{ padding: "5px" }}>
                          <div className="form-check">
                            <input
                              className="form-check-input"
                              type="radio"
                              name="paymentMethod"
                              id="bank"
                              checked={paymentMethod === "bank"}
                              readOnly
                            />
                            <label
                              className="form-check-label fw-bold"
                              htmlFor="bank">
                              Bank Transfer
                            </label>
                          </div>
                          <p className="text-muted small mt-5 mb-0">
                            Direct bank account transfer
                          </p>
                        </div>
                      </div>
                    </div>

                    {/* Add Pay Now button that appears after selecting payment method */}
                    {paymentMethod && (
                      <button
                        className="btn btn-main mt-20 w-100 py-18"
                        onClick={handleProceedToPay}>
                        Proceed to Pay
                      </button>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Payment Modal */}
      {showPaymentModal && (
        <div
          className="modal fade show"
          style={{ display: "block", backgroundColor: "rgba(0,0,0,0.5)" }}>
          <div className="modal-dialog modal-dialog-centered modal-lg">
            <div className="modal-content payment-modal">
              <div className="modal-header">
                <h5 className="modal-title">
                  {paymentMethod === "qr" ? "QR Code Payment" : "Bank Transfer"}
                </h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={closePaymentModal}></button>
              </div>
              <div className="modal-body">
                {paymentMethod === "qr" && (
                  <div className="row">
                    <div className="col-md-6">
                      <h6 className="mb-15">Scan this QR code to pay</h6>
                      <img
                        src={bankDetails.qr_code_image}
                        alt="Payment QR Code"
                        className="img-fluid mb-20"
                      />
                    </div>
                    <div className="col-md-6">
                      <h5 className="text-lg text-muted">
                        Amount: ₹{totalPrice}
                      </h5>
                      <div className="form-group mb-20">
                        <input
                          type="text"
                          className="form-control"
                          placeholder="Enter transaction ID"
                          value={formState.transactionId}
                          onChange={(e) =>
                            setFormState({
                              ...formState,
                              transactionId: e.target.value,
                            })
                          }
                          required
                        />
                      </div>
                      <div className="form-group">
                        <label className="mb-5">
                          Upload Payment Screenshot
                        </label>
                        <div className="file-upload-wrapper">
                          <label className="file-upload-label btn btn-primary w-100">
                            <FaUpload className="me-2" />
                            {screenshotFile
                              ? screenshotFile.name
                              : "Choose File"}
                            <input
                              type="file"
                              className="d-none"
                              accept="image/*"
                              onChange={handleScreenshotUpload}
                              required
                            />
                          </label>
                          {screenshotPreview && (
                            <div className="mt-3 text-center">
                              <img
                                src={screenshotPreview}
                                alt="Payment Screenshot Preview"
                                className="img-thumbnail"
                                style={{ maxHeight: "150px" }}
                              />
                              <button
                                type="button"
                                className="btn btn-sm btn-danger mt-2"
                                onClick={() => {
                                  setScreenshotFile(null);
                                  setScreenshotPreview(null);
                                }}>
                                Remove
                              </button>
                            </div>
                          )}
                        </div>
                        <small className="text-muted">
                          Max file size: 2MB (JPEG, PNG)
                        </small>
                      </div>
                    </div>
                  </div>
                )}

                {paymentMethod === "bank" && (
                  <div className="row">
                    <div className="col-md-6">
                      <h6 className="mb-10">Bank Details</h6>

                      <p className="text-lg">
                        <strong>Account Name:</strong>{" "}
                        {bankDetails.bank_account_holder_name}
                      </p>
                      <p className="text-lg">
                        <strong>Account Number:</strong>{" "}
                        {bankDetails.bank_account}
                      </p>

                      <p className="text-lg">
                        <strong>Bank Name:</strong> {bankDetails.bank_name}
                      </p>
                      <p className="text-lg">
                        <strong>IFSC Code:</strong> {bankDetails.ifsc_code}
                      </p>
                      <p className="text-lg">
                        <strong>Branch:</strong> {bankDetails.branch_name}
                      </p>
                    </div>
                    <div className="col-md-6">
                      <h5 className="text-lg text-muted">
                        Amount: ₹{totalPrice}
                      </h5>
                      <div className="form-group mb-10">
                        <input
                          type="text"
                          className="form-control"
                          placeholder="Enter transaction ID or UTR number"
                          value={formState.transactionId}
                          onChange={(e) =>
                            setFormState({
                              ...formState,
                              transactionId: e.target.value,
                            })
                          }
                          required
                        />
                      </div>
                      <div className="form-group">
                        <label className="mb-5">
                          Upload Payment Screenshot
                        </label>
                        <div className="file-upload-wrapper">
                          <label className="file-upload-label btn btn-primary w-100">
                            <FaUpload className="me-2" />
                            {screenshotFile
                              ? screenshotFile.name
                              : "Choose File"}
                            <input
                              type="file"
                              className="d-none"
                              accept="image/*"
                              onChange={handleScreenshotUpload}
                              required
                            />
                          </label>
                          {screenshotPreview && (
                            <div className="mt-3 text-center">
                              <img
                                src={screenshotPreview}
                                alt="Payment Screenshot Preview"
                                className="img-thumbnail"
                                style={{ maxHeight: "150px" }}
                              />
                              <button
                                type="button"
                                className="btn btn-sm btn-danger mt-2"
                                onClick={() => {
                                  setScreenshotFile(null);
                                  setScreenshotPreview(null);
                                }}>
                                Remove
                              </button>
                            </div>
                          )}
                        </div>
                        <small className="text-muted">
                          Max file size: 2MB (JPEG, PNG)
                        </small>
                      </div>
                    </div>
                  </div>
                )}
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={closePaymentModal}
                  disabled={isLoader}>
                  Cancel
                </button>
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={handlePayNow}
                  disabled={
                    !formState.transactionId ||
                    !screenshotFile ||
                    !paymentMethod ||
                    isLoader
                  }>
                  {isLoader ? <BtnLoader /> : "Pay Now"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Order Success Modal */}
      {orderSuccess && (
        <div
          className="modal fade show"
          style={{ display: "block", backgroundColor: "rgba(0,0,0,0.5)" }}>
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-body text-center p-40">
                <FaCheckCircle className="text-success mb-20" size={60} />
                <h4 className="mb-15">Order Placed Successfully!</h4>
                <p className="mb-20">
                  Your order has been confirmed and will be processed shortly.
                </p>
                <button
                  className="btn btn-success"
                  onClick={() => {
                    setOrderSuccess(false);
                    navigate("/");
                  }}>
                  Continue Shopping
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </WebLayout>
  );
}

export default Checkout;
